import { useCallback, useEffect, useMemo, useState } from 'react';
import { categoriesService } from '../services/api';
import { CategoryType, ContentCategory } from '../types';

export const defaultCategoryForm = {
  name: '',
  slug: '',
  type: 'COURSE' as CategoryType,
  parentId: '',
  description: '',
  shortDescription: '',
  image: '',
  icon: '',
  color: '',
  metaTitle: '',
  metaDescription: '',
  metaKeywordsText: '',
  canonicalUrl: '',
  robots: 'index,follow',
  ogTitle: '',
  ogDescription: '',
  ogImage: '',
  twitterCard: 'summary_large_image',
  twitterTitle: '',
  twitterDescription: '',
  twitterImage: '',
  schemaType: 'CollectionPage',
  sortOrder: 0,
  isActive: true,
  isFeatured: false,
  browseLandingIntro: false,
  /** UI-only: on save, copy `image` to ogImage + twitterImage */
  syncCoverToSocial: true,
};

export type CategoryFormState = typeof defaultCategoryForm;

const normalizeError = (err: unknown, fallback: string) => {
  const anyErr = err as { response?: { data?: { message?: string | string[] } } };
  const message = anyErr?.response?.data?.message || fallback;
  return Array.isArray(message) ? message.join(', ') : message;
};

export const buildCategoryTree = (items: ContentCategory[]): ContentCategory[] => {
  const map = new Map<string, ContentCategory & { children: ContentCategory[] }>();
  items.forEach((item) => map.set(item.id, { ...item, children: [] }));
  const roots: ContentCategory[] = [];
  map.forEach((item) => {
    if (item.parentId && map.has(item.parentId)) {
      map.get(item.parentId)!.children!.push(item);
    } else {
      roots.push(item);
    }
  });
  return roots.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
};

export function useCategoriesManager() {
  const [categories, setCategories] = useState<ContentCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editing, setEditing] = useState<ContentCategory | null>(null);
  const [form, setForm] = useState<CategoryFormState>(defaultCategoryForm);
  const [activeTab, setActiveTab] = useState<CategoryType>('COURSE');
  const [moveTarget, setMoveTarget] = useState<ContentCategory | null>(null);
  const [moveParentId, setMoveParentId] = useState<string>('');

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const data = await categoriesService.getAll();
      setCategories(data);
    } catch (err: unknown) {
      setError(normalizeError(err, 'خطا در دریافت دسته‌بندی‌ها'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const filtered = useMemo(
    () => categories.filter((item) => item.type === activeTab),
    [categories, activeTab],
  );

  const categoryTree = useMemo(() => buildCategoryTree(filtered), [filtered]);

  const parentOptions = useMemo(
    () =>
      categories
        .filter((item) => item.type === form.type && item.id !== editing?.id)
        .sort((a, b) => (a.path || '').localeCompare(b.path || '')),
    [categories, form.type, editing?.id],
  );

  const moveOptions = useMemo(
    () =>
      categories
        .filter((item) => item.type === moveTarget?.type && item.id !== moveTarget?.id)
        .sort((a, b) => (a.path || '').localeCompare(b.path || '')),
    [categories, moveTarget],
  );

  const resetAndClose = useCallback(() => {
    setIsModalOpen(false);
    setEditing(null);
    setForm(defaultCategoryForm);
  }, []);

  const openCreate = useCallback(() => {
    setEditing(null);
    setForm({ ...defaultCategoryForm, type: activeTab });
    setIsModalOpen(true);
  }, [activeTab]);

  const openEdit = useCallback((category: ContentCategory) => {
    setEditing(category);
    const cover = category.image || '';
    const og = category.ogImage || '';
    const tw = category.twitterImage || '';
    const socialMatchesCover =
      Boolean(cover) && ((!og || og === cover) && (!tw || tw === cover));
    setForm({
      name: category.name || '',
      slug: category.slug || '',
      type: category.type,
      parentId: category.parentId || '',
      description: category.description || '',
      shortDescription: category.shortDescription || '',
      image: cover,
      icon: category.icon || '',
      color: category.color || '',
      metaTitle: category.metaTitle || '',
      metaDescription: category.metaDescription || '',
      metaKeywordsText: (category.metaKeywords || []).join(', '),
      canonicalUrl: category.canonicalUrl || '',
      robots: category.robots || 'index,follow',
      ogTitle: category.ogTitle || '',
      ogDescription: category.ogDescription || '',
      ogImage: og,
      twitterCard: category.twitterCard || 'summary_large_image',
      twitterTitle: category.twitterTitle || '',
      twitterDescription: category.twitterDescription || '',
      twitterImage: tw,
      schemaType: category.schemaType || 'CollectionPage',
      sortOrder: Math.max(0, Number.isFinite(Number(category.sortOrder)) ? Number(category.sortOrder) : 0),
      isActive: category.isActive,
      isFeatured: category.isFeatured ?? false,
      browseLandingIntro: category.browseLandingIntro ?? false,
      syncCoverToSocial: socialMatchesCover || (!og && !tw),
    });
    setIsModalOpen(true);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setIsSaving(true);
      setError('');
      try {
        const cover = form.image.trim();
        const useCoverForSocial = form.syncCoverToSocial && Boolean(cover);
        const payload = {
          name: form.name.trim(),
          slug: form.slug.trim(),
          type: form.type,
          // Explicit null so backend clears FK (undefined is omitted and used to keep old parent).
          parentId: form.parentId.trim() ? form.parentId.trim() : null,
          description:
            (form.description ?? '').trim().length > 0 ? form.description ?? '' : undefined,
          shortDescription: form.shortDescription.trim() || undefined,
          image: cover || undefined,
          icon: form.icon.trim() || undefined,
          color: form.color.trim() || undefined,
          metaTitle: form.metaTitle.trim() || undefined,
          metaDescription: form.metaDescription.trim() || undefined,
          metaKeywords: form.metaKeywordsText
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean),
          canonicalUrl: form.canonicalUrl.trim() || undefined,
          robots: form.robots.trim() || undefined,
          ogTitle: form.ogTitle.trim() || undefined,
          ogDescription: form.ogDescription.trim() || undefined,
          ogImage: useCoverForSocial ? cover : form.ogImage.trim() || undefined,
          twitterCard: form.twitterCard.trim() || undefined,
          twitterTitle: form.twitterTitle.trim() || undefined,
          twitterDescription: form.twitterDescription.trim() || undefined,
          twitterImage: useCoverForSocial ? cover : form.twitterImage.trim() || undefined,
          schemaType: form.schemaType.trim() || undefined,
          sortOrder: (() => {
            const n = Number(form.sortOrder);
            if (!Number.isFinite(n)) return 0;
            return Math.max(0, Math.floor(n));
          })(),
          isActive: form.isActive,
          isFeatured: form.isFeatured,
          browseLandingIntro: form.browseLandingIntro,
        };

        if (editing) {
          await categoriesService.update(editing.id, payload);
        } else {
          await categoriesService.create(payload as any);
        }
        await fetchCategories();
        resetAndClose();
      } catch (err: unknown) {
        setError(normalizeError(err, 'خطا در ذخیره دسته‌بندی'));
      } finally {
        setIsSaving(false);
      }
    },
    [editing, fetchCategories, form, resetAndClose],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (!window.confirm('\u0622\u06cc\u0627 \u0627\u0632 \u062d\u0630\u0641 \u0627\u06cc\u0646 \u062f\u0633\u062a\u0647\u200c\u0628\u0646\u062f\u06cc \u0645\u0637\u0645\u0626\u0646 \u0647\u0633\u062a\u06cc\u062f\u061f')) return;
      try {
        await categoriesService.remove(id);
        await fetchCategories();
      } catch (err: unknown) {
        setError(normalizeError(err, 'خطا در حذف دسته‌بندی'));
      }
    },
    [fetchCategories],
  );

  const handleMove = useCallback(async () => {
    if (!moveTarget) return;
    try {
      await categoriesService.move(moveTarget.id, moveParentId || null);
      setMoveTarget(null);
      setMoveParentId('');
      await fetchCategories();
    } catch (err: unknown) {
      setError(normalizeError(err, 'خطا در جابه‌جایی دسته‌بندی'));
    }
  }, [fetchCategories, moveParentId, moveTarget]);

  return {
    categories,
    loading,
    error,
    setError,
    isModalOpen,
    isSaving,
    editing,
    form,
    setForm,
    activeTab,
    setActiveTab,
    moveTarget,
    setMoveTarget,
    moveParentId,
    setMoveParentId,
    filtered,
    categoryTree,
    parentOptions,
    moveOptions,
    fetchCategories,
    resetAndClose,
    openCreate,
    openEdit,
    handleSubmit,
    handleDelete,
    handleMove,
  };
}

export type CategoriesManager = ReturnType<typeof useCategoriesManager>;
